import { useEffect, useRef, useState } from 'react';
import { useUpdateFlexiRequestStatus } from '../hooks/mutations';
import { BtnDefault, BtnPrimary } from './common/Buttons';
import { formatDuration, formatTimeHandler } from '../utils';
import { getFlexiStatusColor } from './flexi-requests/utils';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { FlexiRequestDetailsProps } from '../types/flexi';
import moment from 'moment';


const FlexiRequestDetails = ({
  flexiRequest,
  onUpdate,
  onUpdateError,
}: FlexiRequestDetailsProps) => {
  const [comment, setComment] = useState('');
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [startTime, setStartTime] = useState('');
  const [stopTime, setStopTime] = useState('');
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (flexiRequest) {
      setStartTime(moment(flexiRequest.startTime).format('YYYY-MM-DDTHH:mm'));
      setStopTime(moment(flexiRequest.stopTime).format('YYYY-MM-DDTHH:mm'));
    }
  }, [flexiRequest?.startTime, flexiRequest?.stopTime]);

  const { mutate: updateStatus, isPending } = useUpdateFlexiRequestStatus({
    onSuccess: () => {
      if (isMountedRef.current) setLoadingAction(null);
      onUpdate();
    },
    onError: () => {
      if (isMountedRef.current) setLoadingAction(null);
      onUpdateError();
    },
  });

  if (!flexiRequest) return null;

  const handleAction = (status: 'approved' | 'rejected') => {
    if (status === 'rejected' && !comment.trim()) return;
    setLoadingAction(status);
    updateStatus({
      _id: flexiRequest._id,
      status,
      comment: comment || undefined,
      startTime: startTime ? new Date(startTime).toISOString() : undefined,
      stopTime: stopTime ? new Date(stopTime).toISOString() : undefined,
    });
  };

  const isActionable = flexiRequest.status === 'pending';

  return (
    <div className="p-6 space-y-6">
      {/* Status Badge */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Flexi Shift Request</h3>
        <span
          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getFlexiStatusColor(flexiRequest.status)}`}
        >
          {flexiRequest.status.toUpperCase()}
        </span>
      </div>

      {/* Request Details */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-600">Agent</label>
            <p className="text-base text-gray-900">{flexiRequest.agentName}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Brand</label>
            <p className="text-base text-gray-900">{flexiRequest.brandName}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Agent ID</label>
            <p className="text-base text-gray-900">{flexiRequest.kimId}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Timezone</label>
            <p className="text-base text-gray-900">{flexiRequest.timeZone}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Login Time</label>
            <p className="text-base text-gray-900">
              {formatTimeHandler(flexiRequest.startTime, flexiRequest.timeZone)}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Logout Time</label>
            <p className="text-base text-gray-900">
              {formatTimeHandler(flexiRequest.stopTime, flexiRequest.timeZone)}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Work Duration</label>
            <p className="text-base text-gray-900">{formatDuration(flexiRequest.workDuration)}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Break Duration</label>
            <p className="text-base text-gray-900">{formatDuration(flexiRequest.breakDuration)}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Submitted</label>
            <p className="text-base text-gray-900">
              {formatTimeHandler(flexiRequest.createdAt, flexiRequest.timeZone)}
            </p>
          </div>
        </div>

        {flexiRequest.breaks && flexiRequest.breaks.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-600">Breaks</label>
            <div className="mt-1 space-y-1">
              {flexiRequest.breaks.map((b, i) => (
                <div key={i} className="text-sm text-gray-700 bg-gray-50 p-2 rounded flex justify-between">
                  <span>{b.type}</span>
                  <span>{formatDuration(b.duration)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {flexiRequest.comment && (
          <div>
            <label className="text-sm font-medium text-gray-600">Comment</label>
            <p className="text-base text-gray-900 bg-gray-50 p-3 rounded">
              {flexiRequest.comment}
            </p>
          </div>
        )}

        {flexiRequest.reviewedBy && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Reviewed By</label>
              <p className="text-base text-gray-900">{flexiRequest.reviewedBy.name}</p>
            </div>
            {flexiRequest.reviewedAt && (
              <div>
                <label className="text-sm font-medium text-gray-600">Reviewed At</label>
                <p className="text-base text-gray-900">
                  {formatTimeHandler(flexiRequest.reviewedAt, flexiRequest.timeZone)}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Section */}
      {isActionable && (
        <div className="border-t pt-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {([
              { label: 'Login Start Time', value: startTime, onChange: setStartTime },
              { label: 'Login Stop Time', value: stopTime, onChange: setStopTime },
            ] as const).map(({ label, value, onChange }) => (
              <div key={label}>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{label}</label>
                <input
                  type="datetime-local"
                  className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  onClick={(e) => (e.target as HTMLInputElement).showPicker()}
                />
              </div>
            ))}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Comment (required for rejection)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 focus:outline-none"
              rows={3}
              placeholder="Add a comment about this flexi shift request..."
            />
          </div>

          <div className="flex gap-3">
            <BtnPrimary
              onClick={() => handleAction('approved')}
              disabled={isPending}
              className="flex items-center gap-2"
            >
              {loadingAction === 'approved' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CheckCircle size={16} />
              )}
              Approve
            </BtnPrimary>

            <BtnDefault
              onClick={() => handleAction('rejected')}
              disabled={isPending || !comment.trim()}
              className="flex items-center gap-2 !bg-red-100 !text-red-700 hover:!bg-red-200"
            >
              {loadingAction === 'rejected' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <XCircle size={16} />
              )}
              Reject
            </BtnDefault>
          </div>
        </div>
      )}

      {!isActionable && (
        <div className="border-t pt-6">
          <p className="text-sm text-gray-600">
            This flexi shift request has already been {flexiRequest.status}.
          </p>
        </div>
      )}
    </div>
  );
};

export default FlexiRequestDetails;
